/**
 *  * src/utils/undoRedo/commands/BellCommand.js
 * 
 * BellCommand.js
 * Bell (Zil) + Bell Trafo + Feeder ekleme/silme komutları
 *
 * Bell eklenirken:
 * 1. Bell load panele eklenir (Redux simpleSupplySlice)
 * 2. Bell trafo sahneye eklenir (panel çıkış noktasında)
 * 3. Bell symbol sahneye eklenir (wire sonunda)
 * 4. Wire (polyline) sahneye eklenir
 * 5. Wire markers (kablo sayısı göstergeleri) sahneye eklenir
 */

import BaseCommand from './BaseCommand';
import { addLoadToComponent, removeComponent, removeFeeder } from '../../../redux/simpleSupplySlice';
import { addWire, deleteWire } from '../../../redux/wireSlice';
import * as THREE from 'three';
import { createBell, createBellTrafo } from '../../../factory/symbols/createBellSymbols';
import sceneObjectCache from '../SceneObjectCache';
import { batch } from 'react-redux';

class BellCommand extends BaseCommand {
  /**
   * @param {string} action - 'ADD' veya 'DELETE'
   * @param {object} bellLoadData - Bell load component verisi (simpleSupplySlice için)
   * @param {object} feederData - Feeder verisi
   * @param {object} sceneData - Three.js scene verisi (vertices, bell trafo position, etc.)
   */
  constructor(action, bellLoadData, feederData, sceneData) {
    super(action === 'ADD' ? 'ADD_BELL' : 'DELETE_BELL', {
      before: action === 'DELETE' ? { bellLoad: bellLoadData, feeder: feederData } : null,
      after: action === 'ADD' ? { bellLoad: bellLoadData, feeder: feederData } : null
    }, sceneData);

    this.action = action; // 'ADD' veya 'DELETE'

    // ✅ Restore metadata (UNDO sonrası local undo moduna geçiş için)
    this.restoreVertices = (sceneData && (sceneData.restoreVertices || sceneData.vertices)) || [];
    this.restoreSource = (sceneData && sceneData.restoreSource) || null;
    this.restoreCallback = (sceneData && sceneData.restoreCallback) || null;
  }

  /**
   * Bell + Bell Trafo + Feeder ekleme/silme işlemini gerçekleştir
   * @param {boolean} skipRedux - Wire çizimi sırasında Redux'u atla (performans için)
   */
  execute(scene, dispatch, skipRedux = false) {
    if (this.action === 'ADD') {
      this._addBell(scene, dispatch, skipRedux);
    } else if (this.action === 'DELETE') {
      this._deleteBell(scene, dispatch, skipRedux);
    }
  }

  /**
   * Bell + Bell Trafo + Feeder ekleme/silme işlemini geri al
   */
  undo(scene, dispatch) {
    if (this.action === 'ADD') {
      // ADD işlemini geri al = DELETE yap
      // ❌ keepTrafo KALDIRILDI - bellTrafo da silinmeli
      // Çünkü restore sırasında handleFirstPoint DOĞRU KONUMDA yeni bellTrafo oluşturacak
      // keepTrafo=true olursa duplicate bellTrafo oluşuyor!
      this._deleteBell(scene, dispatch, false);
    } else if (this.action === 'DELETE') {
      // DELETE işlemini geri al = ADD yap
      this._addBell(scene, dispatch);
    }
  }

  /**
   * Sahneye Bell + Bell Trafo + Feeder ekle
   * @param {boolean} skipRedux - Wire çizimi sırasında Redux'u atla
   */
  _addBell(scene, dispatch, skipRedux = false) {
    const { bellLoad: bellLoadData, feeder: feederData } = this.data.after || this.data.before;

    // Wire rengi sceneData'dan al
    const wireColor = this.sceneData.color || 0x0045ff;

    // 1. Redux'a load + feeder ekle (skipRedux=false ise)
    if (!skipRedux) {
      batch(() => {
        dispatch(addLoadToComponent({
          parentComponentId: bellLoadData.parentComponentId,
          loadData: bellLoadData,
          feederData: feederData
        }));
      });
    }

    // ❌ DEVRE DIŞI: Bell Trafo ekleme - useBellWiring.js zaten trafo ekliyor (satır 151)
    // 2. Three.js sahnesine bell trafo ekle (panel çıkış noktasında)
    // const trafoPosition = new THREE.Vector3(
    //   this.sceneData.trafoPosition[0],
    //   this.sceneData.trafoPosition[1],
    //   this.sceneData.trafoPosition[2]
    // );

    // const direction = new THREE.Vector3(
    //   this.sceneData.direction[0],
    //   this.sceneData.direction[1],
    //   this.sceneData.direction[2] || 0
    // );

    // const wireColor = this.sceneData.color || 0x0045ff;

    // const bellTrafo = createBellTrafo(
    //   scene,
    //   trafoPosition,
    //   wireColor,
    //   false, // isPreview
    //   direction
    // );

    // // ✅ Trafo userData'sını güncelle
    // bellTrafo.userData = {
    //   ...bellTrafo.userData,
    //   id: this.sceneData.trafoId,
    //   branchID: this.sceneData.branchID,
    //   type: 'BellTrafo',
    //   isElectrical: true
    // };

    // scene.add(bellTrafo);

    // // ✅ Cache'e ekle
    // sceneObjectCache.addFixture(this.sceneData.trafoId, bellTrafo);

    // 3. Three.js sahnesine bell symbol ekle (wire sonunda)
    const bellPosition = new THREE.Vector3(
      this.sceneData.bellPosition[0],
      this.sceneData.bellPosition[1],
      this.sceneData.bellPosition[2]
    );

    // Reuse existing bell symbol if preview already created it (avoid duplicates)
    let bellSymbol = null;
    try {
      // Prefer cached fixture
      bellSymbol = sceneObjectCache.getFixture(this.sceneData.bellId) || scene.getObjectByProperty('uuid', this.sceneData.bellId);
    } catch (e) {
      bellSymbol = scene.getObjectByProperty('uuid', this.sceneData.bellId);
    }

    if (bellSymbol) {
      // ensure rotation and userData are updated to final values
      const bellAngle = this.sceneData.bellAngle || 0;
      bellSymbol.rotation.z = bellAngle;
      bellSymbol.userData = {
        ...bellSymbol.userData,
        id: this.sceneData.bellId,
        branchID: this.sceneData.branchID,
        wireId: this.sceneData.wireId,
        type: 'Bell',
        isElectrical: true,
        power: bellLoadData.power,
        bellType: bellLoadData.name
      };
    } else {
      bellSymbol = createBell(
        scene,
        bellPosition,
        wireColor,
        false // isPreview
      );

      // set final rotation and userData on newly created symbol
      const bellAngle = this.sceneData.bellAngle || 0;
      bellSymbol.rotation.z = bellAngle;
      bellSymbol.userData = {
        ...bellSymbol.userData,
        id: this.sceneData.bellId,
        branchID: this.sceneData.branchID,
        wireId: this.sceneData.wireId,
        type: 'Bell',
        isElectrical: true,
        power: bellLoadData.power,
        bellType: bellLoadData.name
      };

      scene.add(bellSymbol);
      // Cache newly created bell
      sceneObjectCache.addFixture(this.sceneData.bellId, bellSymbol);
    }

    // Ensure existing bellTrafo (if any) is associated and colored correctly
    try {
      const trafoId = this.sceneData.trafoId;
      if (trafoId) {
        let trafoObj = null;
        scene.traverse(obj => {
          if (obj.userData?.type === 'BellTrafo' && obj.userData?.id === trafoId) {
            trafoObj = obj;
          }
        });

        if (trafoObj) {
          trafoObj.userData = {
            ...trafoObj.userData,
            branchID: this.sceneData.branchID,
            id: trafoId,
            type: 'BellTrafo'
          };

          // Update materials to use wire color
          const finalWireColor = this.sceneData.color || 0x0045ff;
          trafoObj.traverse(child => {
            if (child.material) {
              if (Array.isArray(child.material)) {
                child.material.forEach(m => { if (m.color && m.color.setHex) m.color.setHex(finalWireColor); });
              } else {
                const m = child.material;
                if (m.color && m.color.setHex) m.color.setHex(finalWireColor);
              }
            }
          });

          // cache the trafo if needed
          sceneObjectCache.addFixture(trafoId, trafoObj);
        }
      }
    } catch (e) {
      console.warn('Error ensuring bellTrafo color/branch on add', e);
    }

    // 4. Three.js sahnesine wire (polyline) ekle
    if (this.sceneData.vertices && this.sceneData.vertices.length > 1) {
      const wireVertices = this.sceneData.vertices.map(v => new THREE.Vector3(v.x, v.y, v.z));

      const wireGeometry = new THREE.BufferGeometry().setFromPoints(wireVertices);
      const wireMaterial = new THREE.LineBasicMaterial({ color: wireColor });
      const wireLine = new THREE.Line(wireGeometry, wireMaterial);

      wireLine.userData = {
        id: this.sceneData.wireId,
        feederId: feederData?.id, // ✅ Redux feeder ID eşleştirmesi için
        type: 'polyline',
        wireId: this.sceneData.wireId,
        branchID: this.sceneData.branchID,
        panelID: bellLoadData.parentComponentId,
        function: 'wireBell',
        isSelectable: true,
        isSnappable: true, // ✅ Bell wire'a snap edilebilir
        snapType: 'bellWire', // ✅ Snap tipi
        bellIds: [this.sceneData.bellId], // ✅ Bell ID'si
        bellIdsMap: { [this.sceneData.bellId]: true } // ✅ Bell ID Map
      };

      scene.add(wireLine);

      // ✅ Wire'ı cache'e ekle
      sceneObjectCache.addFeeder(this.sceneData.wireId, wireLine);

      // ✅ Redux wireSlice'a kaydet (skipRedux=false ise)
      if (!skipRedux) {
        dispatch(addWire({
          id: this.sceneData.wireId,
          sceneUUID: wireLine.uuid,
          type: 'polyline',
          vertices: this.sceneData.vertices,
          layer: 'bell',
          color: wireColor,
          userData: {
            selectable: true,
            parent: 'bell',
            panelID: bellLoadData.parentComponentId,
            branchID: this.sceneData.branchID
          }
        }));
      }
    }

    // 5. Wire markers (kablo sayısı göstergeleri) ekle
    if (this.sceneData.wireMarkers && this.sceneData.wireMarkers.length > 0) {
      this.sceneData.wireMarkers.forEach(markerData => {
        const { start, end } = markerData;
        const startVec = new THREE.Vector3(start.x, start.y, start.z || 0);
        const endVec = new THREE.Vector3(end.x, end.y, end.z || 0);

        const geo = new THREE.BufferGeometry().setFromPoints([startVec, endVec]);
        const mat = new THREE.LineBasicMaterial({ color: wireColor });
        const line = new THREE.Line(geo, mat);

        line.userData = {
          role: 'wireMarker',
          branchID: this.sceneData.branchID,
          wireId: this.sceneData.wireId
        };

        scene.add(line);

        // ✅ Wire marker'ları da cache'e ekle (silme işlemi için)
        if (!this.sceneData.wireMarkerIds) {
          this.sceneData.wireMarkerIds = [];
        }
        this.sceneData.wireMarkerIds.push(line.uuid);
      });
    }
  }

  /**
   * Sahneden Bell + Bell Trafo + Feeder sil
   * @param {boolean} keepTrafo - true ise bellTrafo silinmez (local undo için)
   */
  _deleteBell(scene, dispatch, keepTrafo = false) {
    const { bellLoad: bellLoadData, feeder: feederData } = this.data.after || this.data.before;

    // 1. Redux'tan load + feeder sil (batch ile tek re-render)
    batch(() => {
      dispatch(removeComponent(bellLoadData.id)); // Component ID ile sil
      dispatch(removeFeeder(feederData.id));
      dispatch(deleteWire(this.sceneData.wireId)); // deleteWire kullan
    });

    // 2. Three.js sahnesinden bell trafo sil - userData.id kullanarak bul
    // ✅ keepTrafo=true ise bellTrafo'yu SİLME (local undo için sahnede kalmalı)
    if (!keepTrafo) {
      let trafoToRemove = null;
      scene.traverse(obj => {
        if (obj.userData?.type === 'BellTrafo' && obj.userData?.id === this.sceneData.trafoId) {
          trafoToRemove = obj;
        }
      });

      if (trafoToRemove) {
        trafoToRemove.traverse(child => {
          if (child.geometry) child.geometry.dispose();
          if (child.material) {
            if (Array.isArray(child.material)) {
              child.material.forEach(mat => mat.dispose());
            } else {
              child.material.dispose();
            }
          }
        });

        if (trafoToRemove.parent) {
          trafoToRemove.parent.remove(trafoToRemove);
        } else {
          scene.remove(trafoToRemove);
        }
      }
    } else {

    }

    // 3. Three.js sahnesinden bell symbol sil - ✅ Cache'den al
    const bellToRemove = sceneObjectCache.getFixture(this.sceneData.bellId);

    if (bellToRemove) {
      bellToRemove.traverse(child => {
        if (child.geometry) child.geometry.dispose();
        if (child.material) {
          if (Array.isArray(child.material)) {
            child.material.forEach(mat => mat.dispose());
          } else {
            child.material.dispose();
          }
        }
      });

      if (bellToRemove.parent) {
        bellToRemove.parent.remove(bellToRemove);
      } else {
        scene.remove(bellToRemove);
      }

      // ✅ Cache'den sil
      sceneObjectCache.removeFixture(this.sceneData.bellId);
    }

    // 4. Three.js sahnesinden wire sil - ✅ Cache'den al
    const wireToRemove = sceneObjectCache.getFeeder(this.sceneData.wireId);

    if (wireToRemove) {
      if (wireToRemove.geometry) wireToRemove.geometry.dispose();
      if (wireToRemove.material) wireToRemove.material.dispose();
      scene.remove(wireToRemove);

      // ✅ Cache'den sil
      sceneObjectCache.removeFeeder(this.sceneData.wireId);
    }

    // 5. Wire markers sil
    if (this.sceneData.wireMarkerIds && this.sceneData.wireMarkerIds.length > 0) {
      this.sceneData.wireMarkerIds.forEach(markerUUID => {
        const markerToRemove = scene.getObjectByProperty('uuid', markerUUID);
        if (markerToRemove) {
          if (markerToRemove.geometry) markerToRemove.geometry.dispose();
          if (markerToRemove.material) markerToRemove.material.dispose();
          scene.remove(markerToRemove);
        }
      });
    }
  }

  /**
   * JSON'dan BellCommand oluştur
   */
  static fromJSON(json) {
    const action = json.type === 'ADD_BELL' ? 'ADD' : 'DELETE';
    const { bellLoad, feeder } = json.data.after || json.data.before;
    return new BellCommand(action, bellLoad, feeder, json.sceneData);
  }
}

export default BellCommand;
